import { validGeo } from './fieldOperation';

// #Fase1-10 — Reglas de captura y presentación; los totales los calcula la API.
export const binaryLabels = {
  yes_no: ['Sí', 'No'],
  compliance: ['Cumple', 'No cumple'],
  existence: ['Existe', 'No existe'],
};

export function normalizeAnswer(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function questionType(question) {
  return question?.question_type || question?.type || 'score_scale';
}

/** Existence y Sí/No requieren habilitación explícita, según la decisión del cliente. */
export function canScore(question = {}) {
  const type = questionType(question);
  if (type === 'yes_no') return !!question.favorable_answer && question.scoring_enabled === true;
  if (type === 'existence') return question.scoring_enabled === true;
  return ['score_scale', 'compliance'].includes(type) && question.scoring_enabled !== false;
}

export function answered(question, item = {}) {
  const type = questionType(question);
  if (type === 'info') return true;
  if (type === 'score_scale') return item.score_set === true || item.has_been_scored === true;
  if (binaryLabels[type]) return typeof item.answer_bool === 'boolean';
  if (type === 'numeric') return item.answer_numeric != null;
  if (type === 'date') return !!item.answer_date;
  if (type.startsWith('text_')) return !!String(item.answer_text || '').trim();
  if (type === 'file_upload') return !!(item.evidence_urls?.length || item.evidence?.length);
  return !!item.selected_options?.length;
}

/** Incumplimiento para requisitos; no equivale a hallazgo cuando el tipo no califica. */
export function belowStandard(question, item = {}) {
  if (item.not_applicable || !answered(question, item)) return false;
  const type = questionType(question);
  if (type === 'compliance' || type === 'existence') return item.answer_bool === false;
  if (type === 'yes_no') {
    return !!question.favorable_answer && item.answer_bool !== (question.favorable_answer === 'si');
  }
  const max = question.max_score ?? 10;
  return canScore(question) && max > 0 && item.score / max < 0.6;
}

/** #Fase1-10 — El flag del servidor manda; datos legacy sin flag conservan su regla de hallazgos. */
export function isFinding(question, item = {}, local = false) {
  if (question.visible === false || item.not_applicable || !canScore(question)) return false;
  if (local) return belowStandard(question, item);
  const finding = item.is_finding ?? question.is_finding;
  if (typeof finding === 'boolean') return finding;
  return answered(question, item) && Number.isFinite(item.score) && item.score <= 4;
}

export function answerChoices(question) {
  const type = questionType(question);
  if (binaryLabels[type]) return binaryLabels[type];
  if (['single_choice', 'multiple_choice', 'dropdown'].includes(type)) {
    return (question.options || []).map((option) => option.label);
  }
  return [];
}

/** #Fase1-10 — §10.b acepta cualquier valor en rango, aunque no figure en options. */
export function scaleValues(question) {
  const max = question.max_score ?? 10;
  const options = (question.options || []).map((option) => Number(option.value));
  return [...new Set([0, 2, 4, 6, 8, 10, max, ...options])]
    .filter((value) => Number.isFinite(value) && value >= 0 && value <= max)
    .sort((a, b) => a - b);
}

/** La opción inicial de API no debe sobrevivir a un cambio local de respuesta. */
export function recommendationSuggestion(question, item = {}) {
  if (item.not_applicable) return '';
  const options = question.options;
  if (!Array.isArray(options)) return item._answerEdited ? '' : question.auto_recommendation || '';
  if (!answered(question, item)) return '';
  const selected = item.selected_options || [];
  const matching = options.filter((option) =>
    questionType(question) === 'score_scale'
      ? Number(option.value) === item.score
      : selected.includes(option.id) || selected.includes(option.label)
  );
  return matching
    .map((option) => option.recommendation)
    .filter(Boolean)
    .join('; ');
}

/** Resuelve también dependencias transitivas; una referencia inválida no revela la pregunta. */
export function visibilityMap(questions, items = {}) {
  const byId = new Map();
  questions.forEach((question) => {
    byId.set(String(question.question_id ?? question.id ?? question.criterion_id), question);
  });
  const result = {};
  const visiting = new Set();
  function visible(question) {
    const key = String(question.question_id ?? question.id ?? question.criterion_id);
    if (key in result) return result[key];
    if (visiting.has(key)) return false;
    visiting.add(key);
    const rule = question.visible_if;
    let show = true;
    if (rule) {
      const parent = byId.get(String(rule.question_id));
      const parentKey = parent?.id ?? parent?.criterion_id ?? parent?.question_id;
      const item = parent && (items[parentKey] || items[parent.question_id] || parent);
      const labels = parent && binaryLabels[questionType(parent)];
      const answers = labels
        ? typeof item?.answer_bool === 'boolean'
          ? [labels[item.answer_bool ? 0 : 1]]
          : []
        : item?.selected_options || [];
      const matches = answers.some((answer) =>
        (rule.answers || []).some(
          (expected) => normalizeAnswer(answer) === normalizeAnswer(expected)
        )
      );
      show = !!parent && visible(parent) && !item?.not_applicable && matches;
    }
    visiting.delete(key);
    result[key] = show;
    result[question.id ?? question.criterion_id ?? key] = show;
    return show;
  }
  questions.forEach(visible);
  return result;
}

export function hasPhoto(item) {
  const evidence = item.evidence || [];
  const urls = item.evidence_urls || [];
  const documents = [...evidence, ...urls.map((file_url) => ({ file_url }))];
  return documents.some((document) => {
    const url = typeof document === 'string' ? document : document.file_url;
    const metadata = evidence.find((entry) => entry.file_url === url) || document;
    if (metadata.media_type) return metadata.media_type === 'image';
    const path = String(url || '').split(/[?#]/)[0];
    return /\.(jpe?g|png|gif|webp|heic|avif)$/i.test(path);
  });
}

export function itemState(question, item = {}, visible = true) {
  if (!visible) return { status: 'oculto', missing: [] };
  if (questionType(question) === 'info') return { status: 'informativo', missing: [] };
  if (item.not_applicable) return { status: 'no_aplica', missing: [] };
  if (!answered(question, item)) return { status: 'sin_contestar', missing: [] };
  // #Fase2-B4 — Zona es el respaldo válido cuando no hay permiso o señal GPS.
  const values = {
    observation: !!String(item.notes ?? item.response_notes ?? '').trim(),
    photo: hasPhoto(item),
    geo: !!(item.zone?.id || validGeo(item.location) || (item.evidence || []).some((entry) =>
      entry.zone?.id || validGeo(entry.geo))),
    document: (item.evidence || []).some((entry) => entry.media_type === 'document'),
    recommendation: !!String(item.recommendation || '').trim(),
  };
  const names = {
    observation: 'observacion', photo: 'foto', recommendation: 'recomendacion',
    geo: 'ubicacion', document: 'documento',
  };
  const missing = Object.keys(values)
    .filter((key) => {
      const requirement = question.requirements?.[key];
      const required =
        requirement === 'always' ||
        (requirement === 'below_standard' && belowStandard(question, item));
      return required && !values[key];
    })
    .map((key) => names[key]);
  // #Fase3-G2 — Sin evento no se exige proveedor; sin contestar conserva su retorno temprano.
  if (question.event_id !== null && question.event_id !== '' &&
      question.requires_provider && !belowStandard(question, item) && !item.provider_ids?.length) missing.push('proveedor');
  if (question.requires_provider && belowStandard(question, item) && !values.observation && !missing.includes('observacion')) missing.push('observacion');
  return { status: missing.length ? 'incompleto' : 'completo', missing };
}

const missingLabels = {
  observacion: 'observación',
  foto: 'foto',
  ubicacion: 'ubicación',
  documento: 'documento',
  recomendacion: 'recomendación',
  proveedor: 'proveedor',
};

export function missingLabel(keys) {
  return keys.map((key) => missingLabels[key] || 'requisito pendiente').join(', ');
}

export function reasonLabel(reason = '') {
  if (reason === 'sin_contestar') return 'Sin contestar';
  if (reason.startsWith('faltan:')) return `Falta: ${missingLabel(reason.slice(7).split(','))}`;
  return 'Revisar requisitos pendientes';
}

export function statusLabel(state) {
  if (state.missing?.length) return `Falta: ${missingLabel(state.missing)}`;
  const labels = {
    completo: 'Completo',
    no_aplica: 'No aplica',
    informativo: 'Informativo',
    sin_contestar: 'Sin contestar',
    oculto: 'Oculto',
    incompleto: 'Incompleto',
  };
  return labels[state.status] || 'Pendiente';
}

export function localMissing(questions, items, visible) {
  return questions.flatMap((question) => {
    const key = question.id ?? question.criterion_id ?? question.question_id;
    const state = itemState(question, items[key], visible[key] !== false);
    const required = state.status === 'sin_contestar' && question.is_required !== false;
    if (!required && state.status !== 'incompleto') return [];
    return [
      {
        question_id: question.question_id ?? key,
        code: question.code,
        title: question.title,
        reason: required ? 'sin_contestar' : `faltan:${state.missing.join(',')}`,
      },
    ];
  });
}

export function evaluationProgress(questions, items, visible) {
  const counted = questions.filter(
    (question) => visible[question.id] !== false && questionType(question) !== 'info'
  );
  const resolved = counted.filter((question) =>
    ['completo', 'no_aplica'].includes(itemState(question, items[question.id]).status)
  );
  return { completed: resolved.length, total: counted.length };
}

export function displayStatus(evaluation) {
  return (
    evaluation?.display_status ||
    (evaluation?.status === 'publicada' && evaluation.published_with_block
      ? 'publicada_con_bloqueo'
      : evaluation?.status)
  );
}

export function matchesEvaluationStatus(evaluation, status) {
  if (!status) return true;
  return status === 'publicada_con_bloqueo'
    ? displayStatus(evaluation) === status
    : evaluation.status === status;
}

export function errorMessage(error, fallback = 'No se pudo guardar') {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : detail?.message || fallback;
}

export function missingFromError(error) {
  return error?.response?.data?.missing || error?.response?.data?.detail?.missing || [];
}

/** Un resumen con null es autoritativo; solo listas antiguas usan el porcentaje de raíz. */
export function evaluationPercentage(evaluation) {
  if (!evaluation || evaluation.semaforo === 'sin_calificacion') return null;
  const percentage = evaluation.score_summary
    ? evaluation.score_summary.percentage
    : evaluation.percentage;
  return typeof percentage === 'number' && Number.isFinite(percentage) ? percentage : null;
}

export function percentageLabel(percentage) {
  return typeof percentage === 'number' && Number.isFinite(percentage)
    ? `${percentage.toFixed(1)}%`
    : 'Sin calificación';
}

export function percentageDelta(current, previous) {
  return typeof current === 'number' && typeof previous === 'number'
    ? Number((current - previous).toFixed(1))
    : null;
}

export function summaryText(evaluation) {
  const summary = evaluation?.score_summary;
  if (!summary) return '—';
  return (
    `Calculado sobre ${summary.answered_applicable_count ?? '—'} de ${summary.applicable_count ?? '—'}` +
    ` aplicables (${summary.na_count ?? '—'} no aplican)`
  );
}

export function publicationWarning(evaluation) {
  return evaluation?.is_blocked
    ? `Se publicará con bloqueo: ${evaluation.blocked_items?.length || 0} reactivos bloqueantes no cumplen\n\n`
    : '';
}

export function shouldAutofillDate(item, { readOnly, visible, dirty, attempted }) {
  return (
    !readOnly &&
    visible !== false &&
    !item.not_applicable &&
    !item.answer_date &&
    !dirty &&
    !attempted
  );
}
